<template>
    <Headify :data="headMeta" />

    <div class="min-h-screen bg-white">
        <NavMain :key="route.name" />

        <div class="flex lg:pl-64">
            <main class="h-screen flex-1 overflow-auto focus:outline-hidden">
                <div class="mx-auto max-w-7xl px-2 py-12 sm:px-4 lg:py-4">
                    <div class="py-2">
                        <Breadcrumbs :links="breadcrumbsLinks" />
                        <ListToolbar
                            :data="directory.content"
                            :result="dataFiltered"
                            :search="toolbarConfig.search"
                            :buttons="toolbarConfig.buttons"
                            :index-id="toolbarConfig.indexId"
                        />
                    </div>

                    <AlertBox
                        v-if="dataFiltered.length === 0"
                        state="warning"
                        :icon="MdiInformation"
                        :title="t('notFound')"
                    >
                        <div class="pt-1 text-sm font-normal">
                            {{
                                t('imageListPreviewText', {
                                    count:
                                        typeof directory.content === 'object'
                                            ? directory.content.length
                                            : 0,
                                })
                            }}
                        </div>
                    </AlertBox>

                    <AlertBox
                        v-if="dataFiltered.length > 0"
                        state="info"
                        :icon="MdiInformation"
                        :title="
                            t('imageListPreviewText', {
                                count:
                                    typeof directory.content === 'object'
                                        ? directory.content.length
                                        : 0,
                            })
                        "
                    >
                    </AlertBox>

                    <div
                        v-if="directory.open && dataFiltered.length > 0"
                        class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
                    >
                        <template
                            v-for="(file, index) in dataFiltered"
                            :key="index"
                        >
                            <div
                                class="group/folder flex cursor-pointer flex-col justify-between rounded-md border border-gray-200 p-4 shadow-sm hover:bg-gray-100"
                            >
                                <div class="inline-block">
                                    <img
                                        :src="file.http"
                                        :alt="file.basename"
                                    />
                                </div>
                                <div
                                    class="j text-sm font-semibold break-words text-gray-700 group-hover/folder:text-blue-600"
                                >
                                    {{ file.basename }}
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </main>
        </div>
    </div>
</template>

<script setup>
    import { reactive } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import Headify from '../components/Headify.vue';
    import Breadcrumbs from '../ui/Breadcrumbs.vue';
    import ListToolbar from '../ui/ListToolbar.vue';
    import NavMain from '../ui/NavMain.vue';
    import AlertBox from '../ui/AlertBox.vue';
    import navs from '../composables/navs';
    import { t } from '../composables/i18n';
    import { cloneData } from '../composables/utils';
    import { getTemplates } from '../composables/useTemplates';
    import { loadDirectory, templateDirectoryName, extractFilename } from '../composables/useFiles';
    import { MdiPlus, MdiInformation } from 'materialdesignicons-vue3/icons/';

    const route = useRoute();
    const router = useRouter();
    const templates = (await getTemplates()) || [];
    const validDirs = [];

    if (templates !== false) {
        for (let t in templates) {
            validDirs.push({
                directory: templateDirectoryName(templates[t].response.templateId),
                name: templates[t].response.name,
                id: templates[t].response.templateId,
            });
        }
    }

    let dirname = route.params.dirname || false;

    const directory = reactive({
        open: false,
        name: '',
        realName: '',
        content: [],
    });

    const search = validDirs.find((item) => item.directory == dirname);

    if (dirname !== false && typeof search !== 'undefined') {
        const result = await loadDirectory(dirname);
        directory.name = dirname;
        directory.realName = search.name;
        const preload = typeof result === 'object' ? result : [];
        directory.open = true;

        directory.content = preload.reduce((accumulator, currentValue, currentIndex) => {
            const fileObj = {
                fileId: `f${currentIndex + 1000}`,
                http: currentValue,
                basename: extractFilename(currentValue),
            };
            accumulator.push(fileObj);
            return accumulator;
        }, []);
    }
    /*
const closeDirectory = () => {
    directory.open = false;
    directory.name = '';
    directory.content = [];
};

const openFileDetail = async (file) => {
    fileDetailModal.state.text = t('imageListPreviewText', {
        count: typeof result === 'object' ? directory.content.length : 0,
    });
    fileDetailModal.state.title = `${t('imageListPreview')} ${directory.name}`;
    /* console.log('FilesView.vue: directory load', directory);
    const test = objectToBase64(directory);
    const newDir = await createNewDir(templateId);
    console.log('FilesView.vue: directory base64', newDir);
};
*/
    const dataFiltered = reactive(cloneData(directory.content));

    const fileDetailModal = reactive({
        state: {
            open: false,
            title: `${t('imageListPreview')} ${directory.name}`,
            text: t('imageListPreviewText', {
                count: directory.content.length, //,
            }),
            maxWidthMax: true,
            data: {},
            image: false,
        },
        buttons: [
            {
                title: t('buttonClose'),
                action: () => {
                    fileDetailModal.state.open = false;
                },
                class: ' bg-gray-100 hover:bg-gray-50 text-gray-800',
            },
        ],
    });

    const breadcrumbsLinks = [
        {
            name: 'dashboard',
            title: t('Dashboard'),
        },
        {
            name: 'files',
            title: t('Files'),
        },
        {
            name: 'directory',
            title: directory.realName,
            final: true,
        },
    ];

    const toolbarConfig = reactive({
        indexId: 'fileId',
        search: {
            show: true,
            subject: '',
            keys: ['basename'],
        },
        buttons: [
            {
                action: () => {
                    router.push({
                        name: 'file',
                        params: {
                            filename: '',
                        },
                    });
                },
                show: true,
                title: t('buttonNewFile'),
                icon: MdiPlus,
            },
        ],
    });

    const headMeta = navs().metaFind(route.name);
</script>
